/**
 * SEO/AIO Configuration (Frontend)
 * 
 * SINGLE SOURCE OF TRUTH: system_settings table (setting_key: 'seo_configuration')
 * 
 * This file contains:
 * - Static constants (sitemap endpoints, project refs)
 * - Utility functions that can work without DB access
 * - Re-exports from useSEOConfig hook for dynamic values
 * 
 * For dynamic SEO config (audience, schema types, char limits):
 * @see src/hooks/useSEOConfig.ts - React hook for frontend
 * @see supabase/functions/_shared/config/seo-config-db.ts - Edge function helper
 */

// Import for internal use
import { DEFAULT_SEO_CONFIG as _DEFAULT_SEO_CONFIG } from '@/hooks/useSEOConfig';
import { SUPABASE_URL } from '@/config/supabase';
import { TRIBUTARIO_BASE_URL } from '@/constants/branding';
import { DOMAIN_DEFAULTS } from '@/lib/domainDefaults';

// Domain detection (stable per page load — from SOT module)
const _isTributario = DOMAIN_DEFAULTS.isTributario;

// Re-export types and hook for convenience
export { 
  useSEOConfig, 
  getSchemaAudience, 
  getEntitySchemaType, 
  getRouteSchemaType,
  truncateToLimit,
  DEFAULT_SEO_CONFIG,
  type SEOConfig,
  type SEOConfigBrand,
  type SEOConfigCharLimits,
} from '@/hooks/useSEOConfig';

// =====================================================
// Supabase Configuration
// =====================================================
export const FUNCTIONS_BASE_URL = `${SUPABASE_URL}/functions/v1`;

// =====================================================
// Sitemap Endpoints (for admin dashboard)
// =====================================================
export const SITEMAP_ENDPOINTS = {
  index: `${FUNCTIONS_BASE_URL}/sitemap-index`,
  pages: `${FUNCTIONS_BASE_URL}/sitemap-pages`,
  locations: `${FUNCTIONS_BASE_URL}/sitemap-locations`,
  topics: `${FUNCTIONS_BASE_URL}/sitemap-topics`,
  institutional: `${FUNCTIONS_BASE_URL}/sitemap-institutional`,
  experts: `${FUNCTIONS_BASE_URL}/sitemap-experts`,
  publications: `${FUNCTIONS_BASE_URL}/sitemap-publications`,
  news: `${FUNCTIONS_BASE_URL}/sitemap-news`,
} as const;

// =====================================================
// Sitemap Metadata (for admin dashboard)
// =====================================================
export const SITEMAP_METADATA = [
  { name: 'sitemap-index', key: 'index', label: 'Sitemap Index', icon: 'LayoutGrid', description: 'Master index of all sitemaps' },
  { name: 'sitemap-pages', key: 'pages', label: 'Static Pages', icon: 'FileText', description: 'Core platform pages' },
  { name: 'sitemap-locations', key: 'locations', label: 'Locations', icon: 'Map', description: '251 locations × 36 primary locales' },
  { name: 'sitemap-topics', key: 'topics', label: 'Topics', icon: 'BookOpen', description: '65 topics with localized slugs' },
  { name: 'sitemap-institutional', key: 'institutional', label: 'Institutional', icon: 'Building', description: '6 institutional pages' },
  { name: 'sitemap-experts', key: 'experts', label: 'Experts', icon: 'Users', description: 'Verified expert profiles' },
  { name: 'sitemap-publications', key: 'publications', label: 'Publications', icon: 'FileText', description: 'Published publications' },
  { name: 'sitemap-news', key: 'news', label: 'News', icon: 'Newspaper', description: 'Publications from last 30 days' },
] as const;

// =====================================================
// Brand Configuration
// =====================================================
export const SITE_NAME = _isTributario ? 'Tributário' : 'Tax.Network';
export const SITE_URL = _isTributario ? TRIBUTARIO_BASE_URL : 'https://tax.network';
export const SITE_LOGO = _isTributario
  ? `${SITE_URL}/logo-tributario.png`
  : `${SITE_URL}/logo-tax-network.png`;

// =====================================================
// Default Images (raster PNG for social media compatibility)
// =====================================================
export const DEFAULT_OG_IMAGE = _isTributario
  ? `${SITE_URL}/images/linkedin-fallback-tributario.png`
  : `${SITE_URL}/images/linkedin-fallback-tax-network.png`;
export const DEFAULT_TWITTER_IMAGE = DEFAULT_OG_IMAGE;

// =====================================================
// Author Defaults
// =====================================================
export const DEFAULT_SCHEMA_AUTHOR = SITE_NAME;
export const DEFAULT_PUBLICATION_AUTHOR = 'Editorial Team';
export const DEFAULT_PAGE_AUTHOR = SITE_NAME;

// =====================================================
// Social Cards Configuration
// =====================================================
export const TWITTER_CARD = 'summary_large_image' as const;
export const TWITTER_SITE = _isTributario ? '@TributarioBR' : '@TaxNetwork';
export const TWITTER_CREATOR = _isTributario ? '@TributarioBR' : '@TaxNetwork';
export const OG_TYPE = 'website' as const;
export const OG_SITE_NAME = SITE_NAME;

// =====================================================
// Default Settings
// =====================================================
export const DEFAULT_LOCALE = 'en';
export const DEFAULT_NO_INDEX = false;

// =====================================================
// Publisher Schema (reusable across all pages)
// =====================================================
export const PUBLISHER_SCHEMA = {
  "@type": "Organization",
  "name": SITE_NAME,
  "url": SITE_URL,
  "logo": {
    "@type": "ImageObject",
    "url": SITE_LOGO
  }
} as const;

// =====================================================
// Helper Functions
// =====================================================

/**
 * Truncate text to a maximum length with ellipsis
 */
export const truncate = (text: string, maxLength: number): string => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
};

/**
 * Get full page title with site name suffix
 * Avoids duplication if site name already present
 */
export const getFullTitle = (title: string | undefined | null): string => {
  if (!title) return SITE_NAME;
  return title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
};

// =====================================================
// DERIVED FIELD HELPERS (GOVERNANCE AUDIT 2025-01)
// These fields are no longer tracked in the Matrix but 
// are computed at runtime from other fields
// =====================================================

/**
 * Derive og_title from meta_title (truncate to 60 chars)
 * GOVERNANCE AUDIT: og_title was 75% identical to meta_title across all entities
 */
export const deriveOgTitle = (metaTitle: string | null | undefined): string | null => {
  if (!metaTitle) return null;
  return truncate(metaTitle, 60);
};

/**
 * Derive og_image_alt from translated_primary
 * GOVERNANCE AUDIT: og_image_alt was pattern-based, non-auditable
 * Pattern: "Tax.Network - {translated_primary}"
 */
export const deriveOgImageAlt = (translatedPrimary: string | null | undefined): string => 
  `${SITE_NAME}${translatedPrimary ? ` - ${translatedPrimary}` : ''}`;

/**
 * Get OG image alt text - pattern: "Tax.Network - {name}"
 * @deprecated Use deriveOgImageAlt instead (same logic, clearer name)
 */
export const getOgImageAlt = (name: string | undefined | null): string => 
  `${SITE_NAME}${name ? ` - ${name}` : ''}`;

/**
 * Get Twitter image alt text - pattern: "Tax.Network - {name}"
 */
export const getTwitterImageAlt = (name: string | undefined | null): string => 
  `${SITE_NAME}${name ? ` - ${name}` : ''}`;

/**
 * Get breadcrumb label - just the translated name
 */
export const getBreadcrumbLabel = (translatedName: string): string => 
  translatedName;

/**
 * Get canonical URL (absolute URL)
 */
export const getCanonicalUrl = (path: string): string => {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${SITE_URL}${cleanPath}`;
};

/**
 * Get current page URL with fallback
 */
export const getCurrentUrl = (): string => 
  typeof window !== 'undefined' ? window.location.href : SITE_URL;

/**
 * Get origin with fallback
 */
export const getOrigin = (): string => 
  typeof window !== 'undefined' ? window.location.origin : SITE_URL;

/**
 * Get OG image URL based on entity type and source data
 * Chain: author.image → featured_image → logo
 */
export const getOgImageUrl = (
  sourceType: string,
  sourceData?: { author?: { image?: string | null }; featured_image_url?: string | null; image?: string | null }
): string => {
  if (sourceType === 'publication') {
    // Publication: author image → featured image → logo
    if (sourceData?.author?.image) return sourceData.author.image;
    if (sourceData?.featured_image_url) return sourceData.featured_image_url;
  } else if (sourceType === 'profile_person' || sourceType === 'profile_organization') {
    // Profile: image → logo
    if (sourceData?.image) return sourceData.image;
  }
  return DEFAULT_OG_IMAGE;
};

/**
 * Get schema author based on entity type
 * Publication: author name OR 'Editorial Team'
 */
export const getSchemaAuthor = (
  sourceType: string,
  authorData?: { display_name?: string | null }
): string => {
  if (sourceType === 'publication') {
    return authorData?.display_name || DEFAULT_PUBLICATION_AUTHOR;
  }
  return DEFAULT_SCHEMA_AUTHOR;
};

// =====================================================
// Columns to be deleted from localization_metadata
// These are now computed at runtime in computeSEOFields()
// =====================================================
export const COLUMNS_TO_DROP = [
  'twitter_image_alt',
  'speakable_content',
  'llm_summary',
  'breadcrumb_label',
  'og_locale',
  'og_type',
  'og_image_url',
  'twitter_image_url',
  'canonical_url',
  'schema_type',
  'schema_author',
  'schema_audience',
] as const;

// =====================================================
// Frontend-computed SEO fields (12 runtime fields)
// =====================================================
export const FRONTEND_COMPUTED_FIELDS = COLUMNS_TO_DROP;

// =====================================================
// Compute SEO Fields at Runtime
// =====================================================
export interface SEOSourceData {
  author?: { display_name?: string | null; image?: string | null };
  featured_image_url?: string | null;
  image?: string | null;
}

export interface LocalizationRow {
  source_type: string;
  locale: string;
  translated_slug?: string;
  translated_name?: string;  // Renamed from translated_primary (EN SOT)
  translated_body_short?: string;
  meta_description?: string;  // Added for profile llm_summary derivation
  aio_snippet?: string;
  og_title?: string;
  og_description?: string;
  og_image_alt?: string;
}

export interface ComputedSEOFields {
  og_locale: string;
  og_type: string;
  og_description: string | null;  // Derived from meta_description (truncated to 155 chars)
  og_image_url: string;
  twitter_image_url: string;
  twitter_image_alt: string;
  canonical_url: string;
  breadcrumb_label: string;
  speakable_content: string | null;
  llm_summary: string | null;
  schema_type: string;
  schema_author: string;
  schema_audience: string;
}

/**
 * Compute all 12 runtime SEO fields from localization row + source data
 * Uses DEFAULT_SEO_CONFIG for synchronous access (no async DB fetch needed)
 */
export const computeSEOFields = (
  row: LocalizationRow,
  sourceData?: SEOSourceData
): ComputedSEOFields => {
  const sourceType = row.source_type;
  const schemaType = _DEFAULT_SEO_CONFIG.entitySchemaTypes[sourceType] || 'WebPage';
  const schemaAudience = _DEFAULT_SEO_CONFIG.schemaAudienceByType[sourceType] || _DEFAULT_SEO_CONFIG.schemaAudienceByType.page;
  
  return {
    // OG fields
    og_locale: row.locale.replace('-', '_'),
    og_type: sourceType === 'publication' ? 'article' : OG_TYPE,
    // DERIVED: og_description = truncate(meta_description, 155)
    og_description: row.meta_description 
      ? truncate(row.meta_description, 155) 
      : null,
    og_image_url: getOgImageUrl(sourceType, sourceData),
    twitter_image_url: getOgImageUrl(sourceType, sourceData),
    // STRICT: Return null if field missing, not empty string or fallback
    twitter_image_alt: row.og_image_alt ?? null,
    
    // URL fields - STRICT: No canonical if no translated slug
    canonical_url: row.translated_slug ? getCanonicalUrl(row.translated_slug) : null,
    // STRICT LOCALE MATCHING: Use translated_name only, null if missing (renamed from translated_primary)
    breadcrumb_label: row.translated_name ?? null,
    
    // AIO fields (direct mapping, no fallback)
    // Profile: derive llm_summary from meta_description (translated_body_short is NOT_APPLICABLE)
    speakable_content: row.aio_snippet ?? null,
    llm_summary: row.source_type === 'profile' 
      ? (row.meta_description ?? null)  // Profile: derive from meta_description
      : (row.translated_body_short ?? null),  // Others: use translated_body_short
    
    // Schema fields
    schema_type: schemaType,
    schema_author: getSchemaAuthor(sourceType, sourceData?.author),
    schema_audience: schemaAudience,
  };
};
